import Constants from '@/Constants';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import { usePrevious } from '@/lib/hooks';
import Category from '@/models/Category';
import { CourseType } from '@/models/CourseLessonContentTO';
import List from '@/models/List';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { Identity } from '@/store/identity/types';
import { addCourseInProgramRequest } from '@/store/program/action';
import { LocaleData } from '@/translations/LocaleData';
import PerfectScrollbar from 'perfect-scrollbar';
import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import EmptySection from '../EmptySection';
import Loader from '../Loader';
import SearchCourseList from './SearchCourseList';
import './style.scss';

interface PropsValue {
    value: string;
    setValue: Function;
    programId: number;
    limit: number;
    categories: Category[];
    courseShow: boolean;
    orderBy?: string;
    preReqEnabled: boolean;
    locale: string;
    type: CourseType;
}

const SearchCourse = ({ value, setValue, programId, orderBy, limit, categories, courseShow, preReqEnabled, locale, type }: PropsValue) => {
    const { id } = useParams<{ id: string }>();

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const identity: Identity = useSelector((state: ApplicationState) => state.identity.data);
    const courseAdded: boolean = useSelector((state: ApplicationState) => state.program.courseAdded);
    const coursesOfTypeInProgram: List[] = useSelector((state: ApplicationState) => state.program.courses.filter((c: List) => c.type === type));
    const courseRemoved: boolean = useSelector((state: ApplicationState) => state.program.courseRemoved);
    const lastRemovedCourseId: number | undefined = useSelector((state: ApplicationState) => state.program.lastRemovedCourseId);

    const [searchName, setSearchName] = useState('');
    const [callPagination, setCallPagination] = useState<boolean>(false);
    const [scroll, setScroll] = useState<boolean>(false);
    const [showBottomLoading, setShowBottomLoading] = useState<boolean>(false);
    const [perfectScrollbar, setPerfectScrollbar] = useState<PerfectScrollbar>();
    const [listSize, setListSize] = useState<number>(0);
    const [categoriesName, setCategoriesName] = useState('');
    const [programCategoryCourses, setProgramCategoryCourses] = useState<List[]>([]);
    const [otherCategoryCourses, setOtherCategoryCourses] = useState<List[]>([]);

    const totalCoursesOfTypeInProgram: number = coursesOfTypeInProgram.length;
    const coursesScrollBarId: string = `all-scrollbar-course-${type.toLocaleLowerCase()}`;
    const categoryIds = categories.map((category) => category.id).join(',');
    const programHasCourseType: boolean = totalCoursesOfTypeInProgram > 0;
    const prevTotalCoursesOfTypeInProgram: number = usePrevious(totalCoursesOfTypeInProgram);

    const getKey = (index: number) => {
        const {
            user: { orgId, applicationId },
        } = identity;

        const offset: number = index * limit;
        return (
            Constants.LMS_COURSES_URI +
            courseService.getQueryParams({
                orgId: orgId,
                applicationId: applicationId,
                name: value,
                status: Constants.programStatus.PUBLISHED,
                orderBy: orderBy ? orderBy : '',
                locale: locale,
                onlyHasProgramClass: 0,
                moduleId: Constants.LMS_MODULE_ID,
                type: type,
                limit: limit,
                offset: offset,
                notInProgramId: programId,
                categoryIds: categoryIds,
                withLessonAmount: true,
                withClassAmount: true,
            })
        );
    };

    const { data: courses, loadMore, total, revalidateResult } = useGetPaginated<List>(getKey);

    const dispatch = useDispatch();

    const updatePerfectScrollbar = () => {
        if (perfectScrollbar) {
            setTimeout(() => {
                perfectScrollbar.update();
            }, 500);
        }
    };

    const callback = useCallback(() => {
        if (total && courses.length < total) {
            setScroll(true);
            setShowBottomLoading(true);
            setCallPagination(!callPagination);
            setScroll(false);
        }
    }, [courses, total]);

    const scrollRef: RefObject<HTMLDivElement> = useBottomScrollListener(callback, { offset: 0 });

    const addCourseInProgram = useCallback(
        (course: List) => {
            const elementId: string = `course_${course.id}`;
            const element: HTMLElement = document.getElementById(elementId) as HTMLElement;
            element.classList.toggle('removing');

            dispatch(addCourseInProgramRequest({ programId: id, course, prerequisites: preReqEnabled }));
            setScroll(true);

            setTimeout(() => {
                element.style.display = 'none';
                updatePerfectScrollbar();
            }, 500);
        },
        [id, preReqEnabled, perfectScrollbar],
    );

    useEffect(() => {
        setPerfectScrollbar(new PerfectScrollbar(`#${coursesScrollBarId}`, { wheelPropagation: false }));
    }, []);

    useEffect(() => {
        if (totalCoursesOfTypeInProgram < prevTotalCoursesOfTypeInProgram) {
            setTimeout(() => {
                revalidateResult();
            }, 1000);
        }
    }, [totalCoursesOfTypeInProgram]);

    useEffect(() => {
        if (courseShow) {
            setValue('');
            updatePerfectScrollbar();
        }
    }, [courseShow]);

    useEffect(() => {
        setScroll(false);
        const delayInput = setTimeout(() => setValue(searchName), 1000);
        return () => clearTimeout(delayInput);
    }, [searchName]);

    useEffect(loadMore, [identity, callPagination]);

    useEffect(() => {
        const programCategoryIds: number[] = categories.map((it) => it.id || -1);

        setListSize(courses.length);

        const programList = courses.filter((course) =>
            programCategoryIds.some((r) => course.categories.map((courseCategory) => courseCategory.id || 0).includes(r)),
        );
        setProgramCategoryCourses(programList);

        const otherList = courses.filter((course) => !programList.includes(course));
        setOtherCategoryCourses(otherList);
    }, [courses.length]);

    useEffect(() => setShowBottomLoading(false), [listSize, total]);

    useEffect(() => {
        if (courseAdded) {
            loadMore();
        }
    }, [courseAdded]);

    useEffect(() => {
        if (courseRemoved) {
            const elementId: string = `course_${lastRemovedCourseId}`;
            const element: HTMLElement = document.getElementById(elementId) as HTMLElement;
            if (element) {
                element.style.display = 'flex';
                setTimeout(() => {
                    element.classList.toggle('removing');
                });
            }
        }
    }, [courseRemoved]);

    useEffect(() => {
        categories && setCategoriesName(categories.map((it) => it.name).join(', '));
    }, [categories]);

    return (
        <div id={coursesScrollBarId} ref={scrollRef}>
            <div className="input-search-program inner-addon left-addon">
                <i className="material-icons-outlined align-middle input-icon">{Constants.icons.SEARCH}</i>
                <input
                    className="form-control"
                    id={`searchInput_${type}`}
                    key={`searchInput_${type}`}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder={strings.SEARCH_FOR_COURSE}
                    type="search"
                    value={searchName}
                />
            </div>

            <SearchCourseList
                courses={programCategoryCourses}
                onAddCourse={addCourseInProgram}
                title={`${strings.SHOW_CATEGORY_COURSE} ${categoriesName}`}
            />

            <SearchCourseList courses={otherCategoryCourses} onAddCourse={addCourseInProgram} title={strings.OTHER_COURSES} />

            {courses.length === 0 && programHasCourseType && (
                <EmptySection iconName={Constants.icons.SMS_FAILED} message={strings.EMPTY_COURSE_LIST} />
            )}

            {showBottomLoading && <Loader />}
        </div>
    );
};

export default SearchCourse;
